import { createReplacePatch, normalizeRules } from 'helpers/patchHelper';
import { logicOperatorTypes } from 'config/constants';
import { graceUnitFor, effectiveHoursOfDay } from 'helpers/recurrenceLimits';

const mapRecurrenceType = (mode) => {
  if (mode === 'schedule') return 'scheduled';
  if (mode === 'manual') return 'manual';
  if (mode === 'none') return 'none';
  return '';
};

const str = (value) => {
  if (value === null || value === undefined) return '';
  return value;
};

const arr = (value) => (Array.isArray(value) ? value : []);

// The API stamps fieldPath / fieldTitle / fieldType itself and ignores isOrphaned, so only the
// four writable keys cross the wire (spec section 1).
const customColumns = (value) => arr(value).map((c) => ({
  source: c.source,
  fieldSUUID: c.fieldSUUID,
  fieldAttribute: c.fieldAttribute,
  afterColumn: c.afterColumn,
}));

// A numeric field is either a number or absent. Empty string and undefined both
// mean "not set" and must reach the API as null, never as 0.
const num = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return value;
};

export const buildInspectionPatches = (config) => {
  if (!config) config = {};

  const recurrence = config.recurrence || {};
  const recurrenceMode = recurrence.mode || null;
  const passFailCriteria = config.passFailCriteria || null;
  const neglectWarnings = config.neglectWarnings || null;

  const assetTypeValueRaw = config.assetTypeValue;
  const assetTypeValue = assetTypeValueRaw && assetTypeValueRaw.trim
    ? assetTypeValueRaw.trim()
    : str(assetTypeValueRaw);

  const isScheduled = recurrenceMode === 'schedule';
  const firstDueAnchor = isScheduled ? str(recurrence.firstDueAnchor) : '';
  // scheduledRecurrenceHoursOfDay only means anything for daily -- computed once via the shared
  // effectiveHoursOfDay predicate (recurrenceLimits.js) and reused below for the grace unit
  // derivation, so both agree on the rule's actual coarseness rather than trusting
  // recurrence.hoursOfDay directly (which could be stale left over from a since-changed frequency).
  const effectiveHours = isScheduled ? effectiveHoursOfDay(recurrence.frequency, recurrence.hoursOfDay) : [];
  // CRITICAL (null vs 0): the API's MigrateLegacyRecurrence runs on save,
  // before validation, and re-seeds a default grace whenever it sees null --
  // so null must mean "never configured" and a deliberate "no grace" has to
  // cross the wire as an explicit 0. num() already keeps that distinction
  // (it nullifies only null/undefined/'', never 0, unlike the num()/str()
  // convention elsewhere on this page where empty means absent). The unit
  // is derived fresh from coarseness whenever a real interval is sent
  // (rather than forwarded from state, which the CadenceEditor and
  // EarlySubmissionEditor normally keep in sync but which this wire boundary should not
  // have to trust) so a mismatched pair can never reach the validator; when
  // there is no real interval, the unit is irrelevant (the API will re-seed
  // both together) so it is sent empty like every other unset field.
  const graceInterval = isScheduled ? num(recurrence.graceInterval) : null;
  const graceUnit = graceInterval !== null ? graceUnitFor(effectiveHours) : '';

  return [
    createReplacePatch('/inspectionRecordsFieldPathHash', str(config.recordsFieldKey)),
    createReplacePatch(
      '/passFailRules/andOr',
      (passFailCriteria && passFailCriteria.andOr) || logicOperatorTypes.AND
    ),
    createReplacePatch(
      '/passFailRules/rules',
      normalizeRules(passFailCriteria && passFailCriteria.rules)
    ),
    createReplacePatch(
      '/passOrFail',
      str(passFailCriteria && passFailCriteria.mode)
    ),
    createReplacePatch('/assetTypeFieldPathHash', str(config.assetTypeFieldKey)),
    createReplacePatch('/assetTypeValue', assetTypeValue),
    createReplacePatch('/recurrenceType', mapRecurrenceType(recurrenceMode)),
    createReplacePatch(
      '/manualRecurrenceDateFieldPathHash',
      recurrenceMode === 'manual' ? str(recurrence.fieldKey) : ''
    ),
    createReplacePatch(
      '/scheduledRecurrenceFrequency',
      isScheduled ? str(recurrence.frequency) : ''
    ),
    createReplacePatch(
      '/scheduledRecurrenceInterval',
      isScheduled ? num(recurrence.interval) : null
    ),
    // Slot arrays are frequency-scoped: only the array the chosen frequency uses
    // is sent, so a rule switched from weekly to monthly cannot leave a stale
    // BYDAY behind on the document.
    createReplacePatch(
      '/scheduledRecurrenceDaysOfWeek',
      isScheduled && recurrence.frequency === 'weekly' ? arr(recurrence.daysOfWeek) : []
    ),
    createReplacePatch('/scheduledRecurrenceHoursOfDay', effectiveHours),
    createReplacePatch(
      '/scheduledRecurrenceDaysOfMonth',
      isScheduled && recurrence.frequency === 'monthly' ? arr(recurrence.daysOfMonth) : []
    ),
    // Yearly has no month field on the resource: an annual rule recurs on the
    // month and day of its start date, so the yearly month/day selectors write
    // startDate. For every other frequency startDate is just the rule's origin.
    createReplacePatch(
      '/scheduledRecurrenceStartDate',
      isScheduled ? num(recurrence.startDate) : null
    ),
    createReplacePatch(
      '/scheduledRecurrenceTimeZone',
      isScheduled ? str(recurrence.timeZone) : ''
    ),
    createReplacePatch('/scheduledFirstDueAnchor', firstDueAnchor),
    createReplacePatch(
      '/scheduledFirstDueDate',
      firstDueAnchor === 'date' ? num(recurrence.firstDueDate) : null
    ),
    createReplacePatch(
      '/scheduledFirstDueFieldPathHash',
      firstDueAnchor === 'field' ? str(recurrence.firstDueFieldKey) : ''
    ),
    createReplacePatch(
      '/scheduledFirstDueOffsetInterval',
      firstDueAnchor === 'field' ? num(recurrence.firstDueOffsetInterval) : null
    ),
    createReplacePatch(
      '/scheduledFirstDueOffsetUnit',
      firstDueAnchor === 'field' ? str(recurrence.firstDueOffsetUnit) : ''
    ),
    createReplacePatch('/scheduledCompletionGraceInterval', graceInterval),
    createReplacePatch('/scheduledCompletionGraceUnit', graceUnit),
    createReplacePatch('/isActiveFieldPathHash', str(config.activeFieldKey)),
    createReplacePatch('/neglectWarningsEnabled', !!neglectWarnings),
    createReplacePatch(
      '/neglectWarningsPeriodInterval',
      neglectWarnings && neglectWarnings.every !== undefined ? neglectWarnings.every : null
    ),
    createReplacePatch(
      '/neglectWarningsPeriodUnit',
      neglectWarnings ? str(neglectWarnings.unit) : ''
    ),
    createReplacePatch('/historyCustomColumns', customColumns(config.historyCustomColumns)),
    createReplacePatch('/scheduledCustomColumns', customColumns(config.scheduledCustomColumns)),
  ];
};
